/**
 * Typed wrappers for G18 /api/v1/entities + /api/v1/auth/switch-entity.
 *
 * Migration note (2026-06-06, Next.js → Vite Phase 2): direct port of
 * frontend/src/lib/api/entities.ts. Uses the shared Vite axios instance
 * (lib/api/client.ts) instead of the Next.js one.
 */

import { api } from "./client";

export interface LegalEntity {
  id: number;
  tenant_id: number;
  name: string;
  code: string;
  country_code: string;
  tax_id?: string;
  is_primary: boolean;
  is_active: boolean;
  registered_address?: Record<string, unknown>;
  currency_code: string;
  fy_start_month: number;
  created_at: string;
  updated_at: string;
}

export interface ListEntitiesResponse {
  items: LegalEntity[];
}

export interface CreateEntityRequest {
  name: string;
  code: string;
  country_code: string;
  tax_id?: string;
  currency_code: string;
  fy_start_month: number;
  registered_address?: Record<string, unknown>;
}

export interface UpdateEntityRequest {
  name?: string;
  country_code?: string;
  tax_id?: string;
  currency_code?: string;
  fy_start_month?: number;
  registered_address?: Record<string, unknown>;
}

export interface SwitchEntityRequest {
  entity_id: number;
}

export interface SwitchEntityResponse {
  active_entity_id: number;
  entity: {
    id: number;
    name: string;
    code: string;
    country_code: string;
    is_primary: boolean;
    currency_code: string;
  };
}

export const ENTITIES_BASE = "/api/v1/entities";

// GET /api/v1/entities
export async function listEntities(): Promise<ListEntitiesResponse> {
  const { data } = await api.get<ListEntitiesResponse>(ENTITIES_BASE);
  return data;
}

// POST /api/v1/entities
export async function createEntity(
  body: CreateEntityRequest,
): Promise<LegalEntity> {
  const { data } = await api.post<LegalEntity>(ENTITIES_BASE, body);
  return data;
}

// PATCH /api/v1/entities/{id}
export async function updateEntity(
  id: number,
  body: UpdateEntityRequest,
): Promise<LegalEntity> {
  const { data } = await api.patch<LegalEntity>(`${ENTITIES_BASE}/${id}`, body);
  return data;
}

// POST /api/v1/entities/{id}/set-primary
export async function setEntityPrimary(id: number): Promise<LegalEntity> {
  const { data } = await api.post<LegalEntity>(
    `${ENTITIES_BASE}/${id}/set-primary`,
  );
  return data;
}

// POST /api/v1/entities/{id}/archive
export async function archiveEntity(id: number): Promise<LegalEntity> {
  const { data } = await api.post<LegalEntity>(
    `${ENTITIES_BASE}/${id}/archive`,
  );
  return data;
}

// POST /api/v1/auth/switch-entity
export async function switchEntity(
  body: SwitchEntityRequest,
): Promise<SwitchEntityResponse> {
  const { data } = await api.post<SwitchEntityResponse>(
    "/api/v1/auth/switch-entity",
    body,
  );
  return data;
}
